import DOMPurify from "dompurify";

// Mermaid is ~250 KB gzipped — heavier than React itself. It's only used
// when the user is in chat AND the agent has emitted a fenced ```mermaid
// block, which is a small fraction of pageviews (login, projects, history,
// settings never need it). We lazy-import on first use so the login /
// projects bundles don't pay the cost.
//
// The lazy import is memoised so a chat with multiple diagrams only
// downloads + initialises once. The promise is shared across renders and
// across the renderMermaid / initMermaid entry points.

type MermaidApi = typeof import("mermaid").default;

let mermaidPromise: Promise<MermaidApi> | null = null;
let initPromise: Promise<MermaidApi> | null = null;

function loadMermaid(): Promise<MermaidApi> {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then((m) => m.default);
  }
  return mermaidPromise;
}

/**
 * Lazy-load Mermaid and apply our dark theme on first call.
 *
 * Returns a promise that resolves to the initialised mermaid instance.
 * Idempotent — repeat calls share the same in-flight promise so a burst
 * of <MermaidDiagram /> mounts during a chat turn doesn't trigger N
 * downloads.
 *
 * Called automatically by `renderMermaid()` — most callers don't need
 * to invoke `initMermaid()` directly. Exposed for tests / warm-up
 * paths that want to start the download eagerly (e.g. on chat-route
 * mount, before the first agent response lands).
 */
export function initMermaid(): Promise<MermaidApi> {
  if (initPromise) return initPromise;
  initPromise = loadMermaid().then((mermaid) => {
    mermaid.initialize({
      startOnLoad: false,
      theme: "dark",
      // Stops Mermaid from injecting its own error SVG into document.body when
      // a diagram fails to parse — we render our own controlled fallback.
      suppressErrorRendering: true,
      // Force native SVG <text> elements instead of <foreignObject>+HTML
      // for labels. Two reasons:
      //   1. The DOMPurify pass below uses USE_PROFILES.svg which strips
      //      <foreignObject> as a defense-in-depth XSS guard. Mermaid's
      //      default htmlLabels:true would render the boxes correctly
      //      but lose every label after sanitisation.
      //   2. Native SVG text exports cleanly to PNG and copies into
      //      external SVG editors without browser-specific HTML
      //      rendering quirks — a precondition for the diagram
      //      download / copy buttons we ship alongside this.
      // We still get our themed font + colours via themeVariables; only
      // the wrapping/multiline behaviour is slightly different (which we
      // don't rely on).
      //
      // The top-level `htmlLabels` is the load-bearing one in v11 — many
      // internal renderers read `getConfig().htmlLabels` directly and
      // never fall through to the per-diagram `flowchart.htmlLabels`
      // (which is now deprecated upstream). Without the top-level
      // switch, subgraph titles + several shape labels still go through
      // foreignObject and get stripped by the SVG-profile sanitiser.
      htmlLabels: false,
      flowchart: { htmlLabels: false },
      class: { htmlLabels: false },
      themeVariables: {
        primaryColor: "#1e3a5f",
        primaryTextColor: "#e2e8f0",
        primaryBorderColor: "#1e3a5f",
        lineColor: "#3b82f6",
        background: "#172035",
        mainBkg: "#172035",
        nodeBorder: "#1e3a5f",
        clusterBkg: "#172035",
        fontSize: "13px",
      },
    });
    return mermaid;
  });
  return initPromise;
}

let idCounter = 0;

/**
 * Remove any DOM nodes Mermaid may have left attached to document.body during
 * a render. Mermaid creates temporary `#d{id}` and `#dmermaid-{id}` divs while
 * computing layout; on a parse failure these can survive the render call and
 * push the page layout. Belt-and-braces cleanup for the id we just used.
 */
function purgeMermaidLeaks(id: string) {
  for (const selector of [`#${id}`, `#d${id}`]) {
    document.querySelectorAll(selector).forEach((el) => {
      // Only sweep nodes Mermaid attached directly to body — never touch our
      // own container which has been re-parented into the React tree.
      if (el.parentElement === document.body) el.remove();
    });
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderError(container: HTMLElement, message: string, source: string) {
  container.innerHTML = `
    <div style="
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.78rem;
      color: #fca5a5;
      background: rgba(239, 68, 68, 0.06);
      border: 1px solid rgba(239, 68, 68, 0.25);
      border-radius: 6px;
      padding: 10px 12px;
      max-width: 100%;
      overflow: hidden;
      text-align: left;
    ">
      <div style="font-weight:600;margin-bottom:4px;color:#ef4444">
        Diagram failed to render
      </div>
      <div style="margin-bottom:6px;white-space:pre-wrap;word-break:break-word">${escapeHtml(message)}</div>
      <details style="margin-top:6px;color:#94a3b8">
        <summary style="cursor:pointer;font-weight:500">Show source</summary>
        <pre style="
          margin:6px 0 0 0;
          padding:8px;
          background:#0a1022;
          border-radius:4px;
          color:#94a3b8;
          font-size:0.72rem;
          max-height:200px;
          overflow:auto;
          white-space:pre;
        ">${escapeHtml(source)}</pre>
      </details>
    </div>
  `;
}

export async function renderMermaid(
  code: string,
  container: HTMLElement,
): Promise<void> {
  const mermaid = await initMermaid();
  idCounter += 1;
  const id = `mermaid-${Date.now()}-${idCounter}`;

  // 1. Validate first. mermaid.parse with suppressErrors returns false on
  //    failure instead of throwing, and never touches the DOM.
  let parseOk = false;
  let parseError = "";
  try {
    const result = await mermaid.parse(code, { suppressErrors: true });
    parseOk = result !== false;
  } catch (err) {
    parseError = err instanceof Error ? err.message : String(err);
  }

  if (!parseOk) {
    purgeMermaidLeaks(id);
    renderError(container, parseError || "Diagram syntax is invalid.", code);
    return;
  }

  // 2. Render. Wrapped in try/catch as a final safety net — even validated
  //    diagrams can occasionally fail in layout/render. Always purge the body
  //    afterwards in case Mermaid left transient nodes behind.
  //
  //    The SVG returned by mermaid.render is built from a string we
  //    already validated via mermaid.parse and from values the LLM emits
  //    on our behalf — so it's "trusted" today. Defense in depth: scrub
  //    the SVG through DOMPurify with the SVG profile before assigning
  //    to innerHTML so any future change to the upstream content path
  //    (e.g. allowing user-supplied diagram code) can't introduce an
  //    XSS vector through a foreignObject / script-on-event-handler /
  //    javascript: URL injection.
  try {
    const { svg } = await mermaid.render(id, code);
    container.innerHTML = DOMPurify.sanitize(svg, {
      USE_PROFILES: { svg: true, svgFilters: true },
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    renderError(container, message, code);
  } finally {
    purgeMermaidLeaks(id);
  }
}
